/**
 * VA Identity Verification Service
 * 
 * Handles QR link submission by VAs and verification approval/rejection by admins.
 */

import supabase from '../utils/supabase.js';
import notificationService from '../utils/notificationService';

export const identityVerificationService = {
  /**
   * VA submits a QR code image link for verification
   */
  async submitQRLink(userId, qrLink) {
    const { data, error } = await supabase
      .from('user_profiles')
      .update({
        identity_verification_qr_link: qrLink,
        identity_verification_status: 'pending',
        identity_verified_by: null,
        identity_verified_at: null,
        identity_verification_notes: null
      })
      .eq('id', userId)
      .eq('role', 'va')
      .select('identity_verification_status, identity_verification_qr_link')
      .single();

    if (error) throw error;
    return data;
  },

  /**
   * Admin/moderator approves or rejects VA verification
   */
  async reviewVerification(targetUserId, adminUserId, action, notes = '') {
    const isApprove = action === 'approve';
    
    const { data, error } = await supabase
      .from('user_profiles')
      .update({
        identity_verification_status: isApprove ? 'verified' : 'rejected',
        identity_verified_by: adminUserId,
        identity_verified_at: new Date().toISOString(),
        identity_verification_notes: notes || null
      })
      .eq('id', targetUserId)
      .select('identity_verification_status')
      .single();

    if (error) throw error;

    // Log admin action
    await supabase
      .from('admin_actions')
      .insert({
        admin_user_id: adminUserId,
        action_type: isApprove ? 'identity_verified' : 'identity_rejected',
        target_user_id: targetUserId,
        reason: notes || (isApprove ? 'Identity verification approved' : 'Identity verification rejected')
      });

    // Notify the VA of the result
    const notificationType = isApprove ? 'identity_verified' : 'identity_rejected';
    await notificationService.notifyVA(targetUserId, notificationType, {
      notes: notes || null,
      actionUrl: 'https://app.vasprouts.com/va-profile'
    });

    return data;
  },

  /**
   * Get verification status for a user
   */
  async getVerificationStatus(userId) {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('identity_verification_status, identity_verification_qr_link, identity_verified_at, identity_verification_notes')
      .eq('id', userId)
      .single();

    if (error) throw error;
    return data;
  }
};
